/**
 * Shop.jsx — Coin Shop page (/shop).
 *
 * Layout
 *   • Sticky wallet header (coins + gems balance, refreshes after purchases).
 *   • Category tabs: Cosmetics / Streak / Boosters / Vault / Mystery.
 *   • Grid of ShopItemCards inside the active tab.
 *   • Inventory link below the grid → /shop/inventory drawer.
 *   • Purchase confirmation dialog with a server-timed undo action on success.
 *
 * Currency rules
 *   • Coins ← gamification economy (already in user_gamification.coins).
 *   • Gems  ← premium currency, only minted by tier upgrades elsewhere.
 *   • Items can be coin-only, gem-only, or mixed.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, Backpack, CheckCircle2, ChevronLeft, Coins, Gem, Gift,
  Loader2, Lock, RefreshCw, RotateCcw, Shield, Sparkles, Zap,
} from 'lucide-react';
import { authFetch, formatApiError } from '../utils/authUtils';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import ShopInventoryDrawer from '../components/ShopInventoryDrawer';
import GiftDialog from '../components/GiftDialog';
import CoinPackPicker from '../components/CoinPackPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatBalance = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—'
);

const readApiError = async (response, fallback) => {
  try {
    const payload = await response.json();
    return payload?.detail == null ? fallback : formatApiError(payload.detail);
  } catch {
    return fallback;
  }
};

const CATEGORIES = [
  { id: 'cosmetic', label: 'Cosmetics', icon: Sparkles, accent: 'fuchsia' },
  { id: 'streak',   label: 'Streak',    icon: Shield,   accent: 'emerald' },
  { id: 'booster',  label: 'Boosters',  icon: Zap,      accent: 'amber'   },
  { id: 'vault',    label: 'Vault',     icon: Lock,     accent: 'violet'  },
  { id: 'mystery',  label: 'Mystery',   icon: Gift,     accent: 'rose'    },
];

const RARITY_TONE = {
  common:     'border-slate-600 text-slate-300',
  rare:       'border-sky-500/60 text-sky-200',
  epic:       'border-fuchsia-500/60 text-fuchsia-200',
  legendary:  'border-amber-400/70 text-amber-200',
};

const ShopItemCard = ({ item, onBuy, onGift, onNeedMore, busy, locked }) => {
  const Icon = (CATEGORIES.find((c) => c.id === item.category) || CATEGORIES[0]).icon;
  const rarity = (item.rarity || 'common').toLowerCase();
  const isAffordable = !!item.affordable;
  const onDeal = !!item.on_deal;
  const pct = onDeal ? Math.round((item.discount_rate || 0) * 100) : 0;

  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-2xl border bg-[#10182f]/90 shadow-lg shadow-black/10 transition-colors ${
        onDeal
          ? 'border-amber-300/60 ring-1 ring-amber-300/20'
          : (RARITY_TONE[rarity] || RARITY_TONE.common)
      } hover:border-violet-300/50 hover:bg-[#141d38]`}
      data-testid={`shop-item-${item.item_id}`}
    >
      {/* Deal sash — overlays the rarity stripe when on sale */}
      {onDeal && (
        <span
          className="absolute top-2 right-0 z-10 pointer-events-none"
          data-testid={`deal-sash-${item.item_id}`}
        >
          <span className="block bg-gradient-to-r from-rose-500 to-amber-400 text-slate-900 text-[10px] font-black tracking-wider uppercase px-2.5 py-0.5 shadow-md">
            -{pct}% Deal
          </span>
        </span>
      )}

      {/* Rarity stripe */}
      <div className={`h-1 ${
        onDeal ? 'bg-gradient-to-r from-rose-500 via-amber-300 to-rose-500'
        : rarity === 'legendary' ? 'bg-gradient-to-r from-amber-400 via-amber-200 to-amber-400'
        : rarity === 'epic'    ? 'bg-gradient-to-r from-fuchsia-500 via-purple-400 to-fuchsia-500'
        : rarity === 'rare'    ? 'bg-gradient-to-r from-sky-500 via-cyan-300 to-sky-500'
        : 'bg-slate-600'
      }`} />

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 text-slate-300 shrink-0" />
            <h3 className="text-sm sm:text-base font-bold text-white leading-tight truncate">{item.name}</h3>
          </div>
          <span className={`text-[9px] uppercase tracking-wider font-bold ${RARITY_TONE[rarity] || ''} px-1.5 py-0.5 rounded border`}>
            {rarity}
          </span>
        </div>
        <p className="text-xs text-gray-400 leading-snug mb-3 line-clamp-3">{item.description}</p>

        <div className="mt-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {item.price_coins > 0 && (
              <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-200" data-testid={`price-coins-${item.item_id}`}>
                <Coins className="w-3.5 h-3.5" /> {item.price_coins}
                {onDeal && item.original_price_coins > item.price_coins && (
                  <span className="text-[11px] text-slate-500 line-through font-medium">{item.original_price_coins}</span>
                )}
              </span>
            )}
            {item.price_gems > 0 && (
              <span className="inline-flex items-center gap-1 text-sm font-bold text-cyan-200" data-testid={`price-gems-${item.item_id}`}>
                <Gem className="w-3.5 h-3.5" /> {item.price_gems}
                {onDeal && item.original_price_gems > item.price_gems && (
                  <span className="text-[11px] text-slate-500 line-through font-medium">{item.original_price_gems}</span>
                )}
              </span>
            )}
          </div>
          <Button
            size="sm"
            disabled={busy || locked}
            onClick={() => (isAffordable ? onBuy(item) : onNeedMore(item))}
            className={`min-h-10 rounded-xl font-semibold shadow-md focus-visible:ring-2 focus-visible:ring-amber-300 ${
              isAffordable
                ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white hover:from-violet-500 hover:to-purple-400'
                : 'bg-gradient-to-r from-amber-400 to-yellow-300 text-slate-950 hover:from-amber-300 hover:to-yellow-200'
            }`}
            aria-label={busy
              ? `Another shop action is in progress; ${item.name} is temporarily unavailable`
              : locked
                ? `Verify the latest wallet and inventory before starting another transaction for ${item.name}`
                : (isAffordable ? `Buy ${item.name}` : `Find more currency for ${item.name}`)}
            data-testid={`buy-btn-${item.item_id}`}
          >
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Working…</>
              : locked
                ? 'Verify first'
                : (isAffordable ? 'Buy' : 'Need more')}
          </Button>
        </div>
        {/* Gift CTA — always enabled. If the buyer can't afford, the server
            returns a clear error which we surface as a toast. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onGift(item); }}
          disabled={busy || locked}
          className="mt-2 inline-flex min-h-10 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-violet-400/40 py-1.5 text-xs font-semibold text-violet-100 transition-colors hover:border-violet-300 hover:bg-violet-400/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:opacity-40"
          aria-label={locked ? `Verify the latest wallet and inventory before gifting ${item.name}` : `Send ${item.name} as a gift`}
          data-testid={`gift-btn-${item.item_id}`}
        >
          <Gift className="w-3 h-3" />
          Send as gift
        </button>
      </div>
    </article>
  );
};

const formatCountdown = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  const s = Math.floor((ms % 6e4) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const DealBanner = ({ rotation }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!rotation) return null;
  const pct = Math.round((rotation.discount_rate || 0) * 100);
  if (pct <= 0) return null;
  // tick is intentionally unused in JSX — re-renders every 30s for countdown
  void tick;
  return (
    <div
      className="mx-4 sm:mx-6 mb-5 p-4 rounded-xl border-2 border-rose-400/60 bg-gradient-to-r from-rose-500/15 via-amber-400/10 to-rose-500/15 flex items-center justify-between gap-4 flex-wrap relative overflow-hidden"
      data-testid="shop-deal-banner"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-rose-500/0 via-rose-500/20 to-rose-500/0 animate-pulse"
        aria-hidden="true"
      />
      <div className="flex items-center gap-3 relative">
        <span className="text-2xl">{rotation.is_weekend ? '🔥' : '⚡'}</span>
        <div>
          <p className="text-sm sm:text-base font-bold text-white tracking-tight">
            {rotation.label || `${pct}% off today`}
          </p>
          <p className="text-[11px] sm:text-xs text-rose-100/80">
            {(rotation.deal_item_ids || []).length} items rotating · refreshes in{' '}
            <span className="font-bold text-amber-200">{formatCountdown(rotation.rotates_at)}</span>
          </p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-500 text-white font-black text-sm tracking-wider shadow-md relative">
        -{pct}% OFF
      </span>
    </div>
  );
};

/**
 * TodaysDealsStrip — horizontal scroller of every on-deal item across
 * every category, so the discounts are surfaced even if the viewer
 * never opens the category tab those items live in. Empty when the
 * server returned no deals for today.
 */
const TodaysDealsStrip = ({ categories, onPickItem, onBuy, busy, locked }) => {
  // Flatten every category's items, keep only on_deal ones, sort by
  // category for predictable order. The strip is fully hidden when
  // empty so it doesn't take vertical space on quiet days.
  const dealItems = [];
  Object.entries(categories || {}).forEach(([catId, items]) => {
    (items || []).forEach((it) => {
      if (it.on_deal) dealItems.push({ ...it, category: it.category || catId });
    });
  });
  if (dealItems.length === 0) return null;

  return (
    <div
      className="px-4 sm:px-6 mb-4"
      data-testid="shop-todays-deals-strip"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-rose-200">
          <span className="text-base">🔥</span> Today's Deals
        </h3>
        <span className="text-[11px] font-bold text-slate-400">
          {dealItems.length} {dealItems.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
        {dealItems.map((it) => {
          const pct = Math.round((it.discount_rate || 0) * 100);
          return (
            <article
              key={it.item_id}
              className="relative w-48 shrink-0 rounded-2xl border border-amber-300/50 bg-gradient-to-br from-[#17203d] to-[#0b1125] p-3 shadow-lg shadow-black/20 transition-colors hover:border-amber-200/80"
              data-testid={`shop-deal-strip-item-${it.item_id}`}
            >
              <span className="absolute -top-2 -right-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-amber-400 text-slate-900 shadow-lg">
                -{pct}%
              </span>
              <p className="text-xs text-rose-200/80 uppercase tracking-widest font-bold truncate">
                {it.category}
              </p>
              <p className="text-sm text-white font-bold mt-0.5 leading-snug line-clamp-2 min-h-[2.5rem]">
                {it.name}
              </p>
              <div className="mt-2 flex min-h-6 flex-wrap items-baseline gap-x-2 gap-y-1">
                {it.price_coins > 0 && (
                  <span className="inline-flex items-baseline gap-1 text-amber-300">
                    <span className="text-base font-black tabular-nums">{it.price_coins}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider">coins</span>
                    {it.original_price_coins > it.price_coins && (
                      <span className="text-[11px] text-slate-500 line-through">{it.original_price_coins}</span>
                    )}
                  </span>
                )}
                {it.price_gems > 0 && (
                  <span className="inline-flex items-baseline gap-1 text-violet-200">
                    <span className="text-base font-black tabular-nums">{it.price_gems}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider">gems</span>
                    {it.original_price_gems > it.price_gems && (
                      <span className="text-[11px] text-slate-500 line-through">{it.original_price_gems}</span>
                    )}
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onPickItem?.(it)}
                  disabled={busy}
                  className="min-h-9 rounded-lg border border-violet-300/30 px-2 text-[11px] font-bold text-violet-100 transition-colors hover:bg-violet-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:cursor-wait disabled:opacity-50"
                >
                  View item
                </button>
                <button
                  type="button"
                  onClick={() => onBuy?.(it)}
                  disabled={busy || locked || !it.affordable}
                  className={`min-h-9 rounded-lg px-2 text-[11px] font-black uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                    it.affordable && !locked
                      ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-slate-950 hover:from-amber-300 hover:to-yellow-200'
                      : 'cursor-not-allowed bg-slate-700/60 text-slate-400'
                  }`}
                  aria-label={busy
                    ? `Another shop action is in progress; ${it.name} is temporarily unavailable`
                    : locked
                      ? `Verify the latest wallet and inventory before starting another transaction for ${it.name}`
                      : (it.affordable ? `Buy ${it.name}` : `${it.name} is not currently affordable`)}
                  data-testid={`shop-deal-strip-buy-${it.item_id}`}
                >
                  {locked ? 'Verify first' : (it.affordable ? 'Buy' : 'More needed')}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

const Shop = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [categories, setCategories] = useState(null);
  const [dealRotation, setDealRotation] = useState(null);
  const [activeTab, setActiveTab] = useState('cosmetic');
  const [confirmItem, setConfirmItem] = useState(null);
  const [giftItem, setGiftItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mutation, setMutation] = useState({ status: 'idle' });
  const mutationInFlightRef = useRef(false);

  const load = useCallback(async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${BACKEND_URL}/api/shop/catalogue`);
      if (!response.ok) {
        throw new Error(await readApiError(response, `Could not load the shop (HTTP ${response.status}).`));
      }
      const data = await response.json();
      if (!data || typeof data !== 'object') throw new Error('The shop returned an invalid response.');
      setWallet(data.wallet && typeof data.wallet === 'object' ? data.wallet : null);
      setCategories(data.categories && typeof data.categories === 'object' ? data.categories : {});
      setDealRotation(data.deal_rotation || null);
      return true;
    } catch (err) {
      setError(err.message || 'Could not load the shop.');
      return false;
    } finally {
      if (background) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabsWithCounts = useMemo(() => CATEGORIES.map((category) => ({
    ...category,
    count: categories ? (categories[category.id] || []).length : null,
  })), [categories]);

  const items = categories?.[activeTab] || [];
  const buying = mutation.status === 'purchasing';
  const mutationInFlight = buying || mutation.status === 'refunding';
  const mutationNeedsReview = mutation.status === 'purchase-error' || mutation.status === 'refund-error';
  const mutationBlocked = mutationNeedsReview || !!mutation.refreshFailed;

  const refundPurchase = async (purchaseId, itemName) => {
    if (!purchaseId || mutationInFlightRef.current) return;
    mutationInFlightRef.current = true;
    setMutation({ status: 'refunding', itemName, purchaseId });
    try {
      const response = await authFetch(`${BACKEND_URL}/api/shop/purchase/${purchaseId}/refund`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, `Could not refund this purchase (HTTP ${response.status}).`));
      }
      await response.json();
      const refreshed = await load({ background: true });
      setMutation({
        status: 'refunded',
        itemName,
        purchaseId,
        refreshFailed: !refreshed,
      });
      toast.success(refreshed ? 'Refund confirmed.' : 'Refund confirmed; balances could not be refreshed.');
    } catch (err) {
      const message = err.message || 'Could not refund this purchase.';
      setMutation({ status: 'refund-error', itemName, purchaseId, message });
      toast.error(message);
    } finally {
      mutationInFlightRef.current = false;
    }
  };

  const performPurchase = async (item) => {
    if (mutationInFlightRef.current) return;
    mutationInFlightRef.current = true;
    setMutation({ status: 'purchasing', itemName: item.name });
    try {
      const response = await authFetch(`${BACKEND_URL}/api/shop/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.item_id }),
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, `Could not complete the purchase (HTTP ${response.status}).`));
      }
      const data = await response.json();
      const refreshed = await load({ background: true });
      setConfirmItem(null);
      setMutation({
        status: 'purchased',
        itemName: item.name,
        purchaseId: data.purchase_id,
        refundableUntil: data.refundable_until,
        refreshFailed: !refreshed,
      });

      const refundMs = new Date(data.refundable_until).getTime() - Date.now();
      const canOfferRefund = data.purchase_id && Number.isFinite(refundMs) && refundMs > 0;
      toast.success(`Purchased: ${item.name}`, {
        ...(canOfferRefund ? {
          action: {
            label: 'Undo',
            onClick: () => refundPurchase(data.purchase_id, item.name),
          },
          duration: refundMs,
        } : {}),
      });
    } catch (err) {
      const message = err.message || 'Could not complete the purchase.';
      setConfirmItem(null);
      setMutation({ status: 'purchase-error', itemName: item.name, message });
      toast.error(message);
    } finally {
      mutationInFlightRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(109,40,217,0.18),transparent_36%),linear-gradient(180deg,#070d1d_0%,#0a1022_48%,#070b17_100%)] pb-28 pt-4 text-white" data-testid="shop-page">
      <div className="mx-auto max-w-7xl">
        <header className="mx-4 mb-6 overflow-hidden rounded-3xl border border-violet-300/20 bg-[#10182f]/90 shadow-2xl shadow-black/25 sm:mx-6">
          <div className="border-b border-white/10 px-4 py-3 sm:px-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
              data-testid="shop-back"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
            </button>
          </div>

          <div className="grid gap-5 bg-gradient-to-br from-violet-950/55 via-[#10182f] to-amber-950/20 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-200">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Rewards marketplace
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white sm:text-5xl">Coin Shop</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Browse the current server catalogue, review the price, and confirm before anything is charged.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Current wallet balances">
              <span className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-amber-300/35 bg-amber-300/10 px-3 text-sm font-bold text-amber-100" data-testid="shop-wallet-coins">
                <Coins className="h-4 w-4" aria-hidden="true" />
                <span><span className="sr-only">Coins: </span>{formatBalance(wallet?.coins)}</span>
              </span>
              <span className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-violet-300/35 bg-violet-300/10 px-3 text-sm font-bold text-violet-100" data-testid="shop-wallet-gems">
                <Gem className="h-4 w-4" aria-hidden="true" />
                <span><span className="sr-only">Gems: </span>{formatBalance(wallet?.gems)}</span>
              </span>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11 rounded-xl border-white/15 bg-white/5 text-slate-100 hover:bg-white/10 hover:text-white focus-visible:ring-amber-300"
                onClick={() => setDrawerOpen(true)}
                data-testid="shop-inventory-btn"
              >
                <Backpack className="mr-1.5 h-4 w-4" aria-hidden="true" /> Inventory
              </Button>
              <button
                type="button"
                onClick={async () => {
                  const refreshed = await load({ background: true });
                  if (refreshed) {
                    setMutation((current) => current.refreshFailed
                      ? { ...current, refreshFailed: false }
                      : current);
                  }
                }}
                disabled={loading || refreshing || mutationInFlight}
                className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:cursor-wait disabled:opacity-60"
                aria-label="Refresh shop and wallet balances"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </header>

        {mutation.status !== 'idle' && (
          <div
            className={`mx-4 mb-5 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm sm:mx-6 ${
              mutation.status === 'purchase-error' || mutation.status === 'refund-error'
                ? 'border-rose-400/35 bg-rose-400/10 text-rose-100'
                : mutation.status === 'purchased' || mutation.status === 'refunded'
                  ? 'border-emerald-300/30 bg-emerald-300/10 text-emerald-100'
                  : 'border-violet-300/30 bg-violet-300/10 text-violet-100'
            }`}
            role={mutation.status.includes('error') ? 'alert' : 'status'}
            aria-live="polite"
            data-testid="shop-mutation-status"
          >
            {mutation.status === 'purchasing' && <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />}
            {mutation.status === 'refunding' && <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />}
            {(mutation.status === 'purchased' || mutation.status === 'refunded') && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            {(mutation.status === 'purchase-error' || mutation.status === 'refund-error') && <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            <div className="min-w-0">
              {mutation.status === 'purchasing' && <p><strong>Purchasing {mutation.itemName}…</strong> Waiting for the server to confirm the charge and grant.</p>}
              {mutation.status === 'purchased' && (
                <p><strong>Purchase confirmed: {mutation.itemName}.</strong> {mutation.refreshFailed ? 'The latest wallet and catalogue could not be reloaded; use Refresh before making another decision.' : 'Wallet and catalogue reloaded from the server.'}</p>
              )}
              {mutation.status === 'refunding' && <p><strong>Refunding {mutation.itemName}…</strong> Waiting for the server to confirm reversal.</p>}
              {mutation.status === 'refunded' && (
                <p><strong>Refund confirmed: {mutation.itemName}.</strong> {mutation.refreshFailed ? 'The latest wallet and catalogue could not be reloaded; use Refresh to verify them.' : 'Wallet and catalogue reloaded from the server.'}</p>
              )}
              {(mutation.status === 'purchase-error' || mutation.status === 'refund-error') && (
                <p><strong>The outcome was not assumed.</strong> {mutation.message} Review your server-loaded wallet and inventory before reloading this page to start another transaction.</p>
              )}
            </div>
          </div>
        )}

      {/* Coin top-up — Stripe (rest of world) / Paystack (Nigeria) */}
      <div id="coin-pack-picker" className="px-4 sm:px-6 mb-6 scroll-mt-20">
        <CoinPackPicker />
      </div>

      {/* Daily/weekend deal banner with live countdown */}
      <DealBanner rotation={dealRotation} />

      {/* "Today's Deals" strip — surfaces every on-deal item across all
          categories so the discounts aren't buried behind a category
          tab the viewer never opens. Tap an item → switches to its tab
          and scrolls it into view; tap "Buy" → opens confirm dialog. */}
      <TodaysDealsStrip
        categories={categories}
        onPickItem={(it) => {
          setActiveTab(it.category);
          // Defer one frame so the new tab's cards mount before we scroll.
          requestAnimationFrame(() => {
            const el = document.querySelector(`[data-testid="shop-item-${it.item_id}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }}
        onBuy={(it) => setConfirmItem(it)}
        busy={mutationInFlight}
        locked={mutationBlocked}
      />

      {/* Category tabs */}
      <div className="sticky top-0 z-10 mb-5 border-y border-white/10 bg-[#080e20]/90 px-4 py-3 backdrop-blur-xl sm:px-6">
        <div className="flex gap-2 overflow-x-auto scrollbar-thin" role="group" aria-label="Shop categories">
          {tabsWithCounts.map((category) => {
            const Icon = category.icon;
            const active = category.id === activeTab;
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveTab(category.id)}
                className={`inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                  active
                    ? 'border-violet-300/60 bg-violet-500/25 text-white shadow-lg shadow-violet-950/30'
                    : 'border-white/10 bg-white/5 text-slate-300 hover:border-violet-300/30 hover:bg-violet-400/10 hover:text-white'
                }`}
                data-testid={`shop-tab-${category.id}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {category.label}
                {category.count !== null && <span className="text-[10px] font-bold opacity-70">{category.count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6">
        {error && categories && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 p-4 text-sm text-amber-100" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div>
              <strong>Refresh failed.</strong> The items below are the last catalogue loaded in this session. {error}
              <button type="button" onClick={() => load({ background: true })} className="ml-2 font-bold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">Retry</button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 py-16 text-center text-sm text-slate-300" role="status">
            <Loader2 className="mr-2 inline-block h-5 w-5 animate-spin" aria-hidden="true" /> Loading the current catalogue and wallet…
          </div>
        ) : error && !categories ? (
          <div className="rounded-2xl border border-rose-400/35 bg-rose-400/10 p-6 text-center text-sm text-rose-100" role="alert">
            <AlertCircle className="mx-auto mb-3 h-7 w-7" aria-hidden="true" />
            <p className="font-semibold">The shop could not be loaded.</p>
            <p className="mt-1 text-rose-100/80">{error}</p>
            <button type="button" onClick={() => load()} className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl border border-rose-200/30 px-4 font-bold hover:bg-rose-100/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 py-16 text-center text-sm text-slate-400">
            <Lock className="mx-auto mb-3 h-8 w-8 text-violet-300/60" aria-hidden="true" />
            <p className="font-semibold text-slate-200">No items are available in this category right now.</p>
            <p className="mt-1">Choose another category or refresh the server catalogue.</p>
          </div>
        ) : (
          <div
            id={`shop-grid-${activeTab}`}
            aria-label={`${CATEGORIES.find((category) => category.id === activeTab)?.label || activeTab} items`}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid={`shop-grid-${activeTab}`}
          >
            {items.map((item) => (
              <ShopItemCard
                key={item.item_id}
                item={item}
                busy={mutationInFlight}
                locked={mutationBlocked}
                onBuy={(it) => setConfirmItem(it)}
                onGift={(it) => setGiftItem(it)}
                onNeedMore={(it) => {
                  // Route the user to where they can earn the missing currency.
                  // Gems are minted by Top Tier subscription only — coins can be
                  // topped up via Stripe/Paystack on the same page.
                  if (it.price_gems > 0 && (wallet?.gems || 0) < (it.price_gems || 0)) {
                    toast.info('💎 Gems unlock via Top Tier — opening upgrade');
                    navigate('/premium#top-tier');
                    return;
                  }
                  // Default: coin shortfall → scroll to the top-up picker.
                  const el = document.getElementById('coin-pack-picker');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    toast.info('💰 Top up coins below to unlock this item');
                  } else {
                    navigate('/profile');
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirm purchase dialog */}
      <Dialog open={!!confirmItem} onOpenChange={(open) => { if (!open && !buying) setConfirmItem(null); }}>
        <DialogContent className="max-w-md border-violet-300/20 bg-[#10182f] text-white shadow-2xl shadow-black/40">
          <DialogHeader>
            <DialogTitle>Confirm purchase</DialogTitle>
            <DialogDescription className="text-slate-400">
              {confirmItem ? (
                <span>
                  Buy <strong className="text-white">{confirmItem.name}</strong> for{' '}
                  {confirmItem.price_coins > 0 && (
                    <span className="inline-flex items-center gap-0.5 font-bold text-amber-200">
                      <Coins className="w-3.5 h-3.5" /> {confirmItem.price_coins}
                    </span>
                  )}
                  {confirmItem.price_coins > 0 && confirmItem.price_gems > 0 && ' + '}
                  {confirmItem.price_gems > 0 && (
                    <span className="inline-flex items-center gap-0.5 font-bold text-cyan-200">
                      <Gem className="w-3.5 h-3.5" /> {confirmItem.price_gems}
                    </span>
                  )}
                  ?
                  <span className="mt-2 block text-xs text-slate-400">This is the current catalogue price. The server validates price, balance, eligibility, and availability when you confirm. Confirmed purchases may be undone only during the server-provided refund window.</span>
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmItem(null)} disabled={buying} className="min-h-11 rounded-xl border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white focus-visible:ring-amber-300" data-testid="shop-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => confirmItem && performPurchase(confirmItem)}
              disabled={buying}
              className="min-h-11 rounded-xl bg-gradient-to-r from-violet-600 to-purple-500 font-semibold text-white hover:from-violet-500 hover:to-purple-400 focus-visible:ring-amber-300"
              data-testid="shop-confirm-buy"
            >
              {buying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Purchasing…</> : 'Confirm purchase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {drawerOpen && (
        <ShopInventoryDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onChange={() => load({ background: true })}
        />
      )}

      <GiftDialog
        open={!!giftItem}
        onOpenChange={(open) => { if (!open) setGiftItem(null); }}
        item={giftItem}
        onSent={() => { load({ background: true }); }}
      />
      </div>
    </div>
  );
};

export default Shop;
